import { APP_CONFIG } from '@/config';
import axios, { AxiosRequestConfig, Method } from 'axios';
import { from, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { utilsService } from './utils.service';
import * as md5 from 'blueimp-md5';
import { cryptoService } from './crypto.service';

interface Params {
  url: string;
  data?: any;
  contentType?: string;
  getParams?: string;
}
export interface CheckFormData {
  /**图片在OSS上的路径（OSS唯一标识） */
  pic_oss_name: string;
  picurl: string;
}
class httpRequestService {
  axios = axios;

  headers = {
    contentType: 'application/json',
    authorization: 'Front',
  };

  constructor() {
    this.initAxios();
  }

  private initAxios() {
    // 添加请求拦截器
    this.axios.interceptors.request.use(
      config => {
        // console.log('req', config);

        return config;
      },
      err => {
        // 对请求错误做些什么
        return Promise.reject(err);
      },
    );

    // 添加响应拦截器
    this.axios.interceptors.response.use(
      res => {
        // console.log('res', res);
        return res;
      },
      err => {
        // 对响应错误做点什么
        return Promise.reject(err);
      },
    );
  }

  private http(params: Params, method: Method) {
    const { url, data, getParams } = params;
    const ContentType = params.contentType || 'application/json';

    const timestamp = Date.now()
      .toString()
      .substr(0, 10);
    const sign = utilsService.raw({ ...params.data, timestamp, private_key: APP_CONFIG.privateKey });
    const headers = { timestamp, sign: md5(sign) };
    // console.log({ headers, sign });

    const config: AxiosRequestConfig = {
      params: getParams,
      url,
      data,
      method,
      headers,
    };

    return of(0).pipe(
      switchMap(() => from(this.axios(config))),
      map(res => res.data),
    );
  }

  // send(url: string, data: any, config?: AxiosRequestConfig) {
  //   return from(axios.post(url, data, { headers: this.headers })).pipe(map(res => res.data));
  // }

  // get(url: string, config?: AxiosRequestConfig) {
  //   return from(axios.get(url, { headers: this.headers })).pipe(map(res => res.data));
  // }

  patch(url: string, data: any, config?: AxiosRequestConfig) {
    return this.http({ url, data }, 'patch');
  }
  post(url: string, data: any, config?: AxiosRequestConfig) {
    return this.http({ url, data }, 'post');
  }

  get(url: string, getParams: any, config?: AxiosRequestConfig) {
    return this.http({ url, getParams }, 'get');
  }

  getOpenId(data: any) {
    const url = APP_CONFIG.wxApi + '/wejoy/openId';
    return from(axios.post(url, data, { headers: this.headers })).pipe(map(res => res.data));
  }

  getUserInfo(data: any) {
    const url = APP_CONFIG.wxApi + '/wejoy/userInfo';
    return from(axios.post(url, { data }, { headers: this.headers })).pipe(map(res => res.data));
  }

  // send(url: string, data: any, config?: AxiosRequestConfig) {
  //   return from(axios.post(url, data, { headers: this.headers })).pipe(map(res => res.data));
  // }
  /** 杜巍后台请求方法 */
  send(url: string, data) {
    const timestamp = Date.now()
      .toString()
      .substr(0, 10);
    const sign = utilsService.raw({ ...data, timestamp });
    const headers = { timestamp, sign: md5(sign) };
    // console.log('send2', timestamp, sign, headers, data);
    return from(axios.post(url, data, { headers })).pipe();
  }

  /** 获取新签名所需headers */
  getNewHeader() {
    const timestamp = Date.now()
      .toString()
      .substr(0, 10);
    const salt = md5(timestamp);

    function getSign(salt: string, timestamp: string) {
      const str = timestamp + salt;
      const n: number = parseInt(timestamp.charAt(9)) > 0 ? parseInt(timestamp.charAt(9)) : 1;
      function uMD5(str, n) {
        if (n < 1) return str;
        // console.log(`uMD5`, n, md5(str));
        return uMD5(md5(str), --n);
      }
      return uMD5(str, n);
    }

    const sign = getSign(salt, timestamp);
    return { contentType: 'application/json', s: salt + timestamp + sign };
  }

  /** 新签名 post */
  newSend(url: string, data) {
    const headers = this.getNewHeader();
    const request = (() => {
      if (cryptoService.isEnable) {
        data = cryptoService.encrypt(data);
        const params = {};
        params[cryptoService.requestKey] = data;
        return from(axios.post(url, params, { headers })).pipe(
          map(res => res.data),
          switchMap(res => of({ data: cryptoService.decrypt(res[cryptoService.responseKey]) })),
        );
      }
      return from(axios.post(url, data, { headers })).pipe();
    })();

    return request.pipe();
  }

  /** 新签名 get */
  newSendGet(url: string, data) {
    const headers = this.getNewHeader();
    const request = (() => {
      if (cryptoService.isEnable) {
        data = cryptoService.encrypt(data);
        const params = {};
        params[cryptoService.requestKey] = data;
        return from(axios.get(url, { params, headers })).pipe(
          map(res => res.data),
          switchMap(res => of({ data: cryptoService.decrypt(res[cryptoService.responseKey]) })),
        );
      }
      return from(axios.get(url, { params: data, headers })).pipe();
    })();

    return request.pipe();
  }

  /** 新签名 put */
  newSendPut(url: string, data) {
    const headers = this.getNewHeader();
    const request = (() => {
      if (cryptoService.isEnable) {
        data = cryptoService.encrypt(data);
        const params = {};
        params[cryptoService.requestKey] = data;
        return from(axios.put(url, params, { headers })).pipe(
          map(res => res.data),
          switchMap(res => of({ data: cryptoService.decrypt(res[cryptoService.responseKey]) })),
        );
      }
      return from(axios.put(url, data, { headers })).pipe();
    })();

    return request.pipe();
  }
}

/** 单例对象函数 */
const service = (() => {
  // 隐藏的Class的构造函数
  // function TodayCilpService() {}

  // 未初始化的单例对象
  let service: httpRequestService;

  return {
    // 创建/获取单例对象的函数
    getInstance: () => {
      if (!service) {
        service = new httpRequestService();
      }
      return service;
    },
  };
})();
export const httpService = service.getInstance();
